import React, { ReactElement, useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as InProgressIcon,
  Error as ErrorIcon,
  Circle as DefaultIcon,
} from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import dayjs from 'dayjs'
import {
  fetchMotherSessionDetail,
  selectMotherSessionDetail,
  selectMotherSessionDetailStatus,
} from './mother.slice'
import OverviewTab from './tabs/OverviewTab'
import TimelineTab from './tabs/TimelineTab'
import GraphTab from './tabs/GraphTab'
import LoreTab from './tabs/LoreTab'
import PlanTab from './tabs/PlanTab'
import type { MotherSessionStatus } from './types'

type SessionDetailProps = {
  selectedSession: string | null
}

let getStatusColor = (
  status: MotherSessionStatus,
): 'success' | 'warning' | 'error' => {
  switch (status) {
    case 'sessionCompleted':
      return 'success'
    case 'sessionInProgress':
      return 'warning'
    case 'sessionFailed':
      return 'error'
    default:
      return 'warning'
  }
}

let getStatusIcon = (status: MotherSessionStatus) => {
  switch (status) {
    case 'sessionCompleted':
      return <CheckCircleIcon color="success" fontSize="small" />
    case 'sessionInProgress':
      return <InProgressIcon color="warning" fontSize="small" />
    case 'sessionFailed':
      return <ErrorIcon color="error" fontSize="small" />
    default:
      return <DefaultIcon color="disabled" fontSize="small" />
  }
}

export default ({ selectedSession }: SessionDetailProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [activeTab, setActiveTab] = useState(0)

  let sessionDetail = useAppSelector(
    selectedSession ? selectMotherSessionDetail(selectedSession) : () => null,
  )
  let sessionDetailStatus = useAppSelector(
    selectedSession
      ? selectMotherSessionDetailStatus(selectedSession)
      : () => 'uninitialized',
  )

  // Load session details when session is selected
  useEffect(() => {
    if (selectedSession && sessionDetailStatus === 'uninitialized') {
      dispatch(fetchMotherSessionDetail(selectedSession))
    }
  }, [dispatch, selectedSession, sessionDetailStatus])

  // Reset tab when session changes
  useEffect(() => {
    setActiveTab(0)
  }, [selectedSession])

  if (!selectedSession) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'text.secondary',
        }}
      >
        <Typography variant="h6">Select a session to view details</Typography>
      </Box>
    )
  }

  if (sessionDetailStatus === 'pending') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (sessionDetailStatus === 'rejected') {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load session details. Please try again.
        </Alert>
      </Box>
    )
  }

  if (!sessionDetail) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'text.secondary',
        }}
      >
        <Typography>Session not found</Typography>
      </Box>
    )
  }

  let { metadata } = sessionDetail

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            flexWrap: 'wrap',
          }}
        >
          {getStatusIcon(metadata.status)}
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontFamily: 'monospace',
              fontSize: { xs: '0.875rem', sm: '1.25rem' },
              wordBreak: 'break-all',
            }}
          >
            {metadata.correlationID}
          </Typography>
          <Chip
            label={metadata.status.replace('Session', '')}
            color={getStatusColor(metadata.status)}
            size="small"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Created: {dayjs(metadata.createdTime).format('MMM D, YYYY h:mm A')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Updated: {dayjs(metadata.updatedTime).fromNow()}
          </Typography>
          {metadata.loreCount > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Lore Count: {metadata.loreCount}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Overview" />
          <Tab label="Plan" disabled={!sessionDetail.plan} />
          <Tab label="Timeline" />
          <Tab label="Execution Graph" disabled={!metadata.hasExecutionGraph} />
          <Tab label="Lore" disabled={metadata.loreCount === 0} />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {activeTab === 0 && <OverviewTab sessionDetail={sessionDetail} />}
        {activeTab === 1 && sessionDetail.plan && (
          <PlanTab sessionDetail={sessionDetail} />
        )}
        {activeTab === 2 && <TimelineTab correlationID={selectedSession} />}
        {activeTab === 3 && metadata.hasExecutionGraph && (
          <GraphTab correlationID={selectedSession} />
        )}
        {activeTab === 4 && metadata.loreCount > 0 && (
          <LoreTab correlationID={selectedSession} />
        )}
      </Box>
    </Box>
  )
}
